import { Col, Row } from 'antd';
import React, { FC, MouseEventHandler, useCallback } from 'react';
import { Company, TaskId } from 'api/models';
import { StatusGroup } from 'api/tasks';
import { Avatar, AvatarGroup } from 'components/atoms/avatar';
import { TaskStatusPicker } from 'components/atoms/tasks/task-status-picker';
import { TaskMoreActions } from 'components/molecules/tasks/more-actions';
import { useIconTypeContext } from 'contexts/icon-type';
import { useSelectedStatusOption } from 'data/fields/hooks';
import { Task } from 'data/tasks';
import { useNavigateToTask } from 'hooks/use-navigate-to-task';
import { TaskCardV2 } from '../task-card-new';
import { ComplatedTaskCard } from './completed-task-card';
import { TaskFooterAdditionalDetails } from './footer-additional-details';
import { FooterAssigneeWithDate } from './footer-assignee-with-date';
import {
  AtRiskIcon,
  Card,
  ContentWrapper,
  MoreActionsCol,
  StatusWrapper,
  TaskCardFooterLeft,
  TaskCardFooterRight,
  TaskCardFooterWrapper,
} from './task-card.styles';
import { TaskCardTitle } from './title';

export const RegularTaskCard: FC<{
  task: Task;
}> = ({ task }) => {
  const navigateToTask = useNavigateToTask();

  const openTask: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      navigateToTask(TaskId(task.project, task.taskId));
    },
    [navigateToTask, task.project, task.taskId],
  );

  return (
    <Card
      bordered={false}
      hoverable
      private={task.private}
      onClick={openTask}
      data-cy={task.taskName}
    >
      <StatusWrapper data-cy="projects.card.status">
        {task.atRisk && <AtRiskIcon />}
        <TaskStatusPicker
          taskId={task.taskId}
          projectId={task.project}
          type={task.type}
          progress={task.progress}
          fields={task.fields}
        />
      </StatusWrapper>

      <ContentWrapper>
        <Row gutter={0} wrap={false}>
          <Col flex="auto">
            <TaskCardTitle task={task} />
          </Col>
          <MoreActionsCol
            flex="32px"
            id="more-actions"
            onClick={(e) => e.stopPropagation()}
          >
            <TaskMoreActions data={task} />
          </MoreActionsCol>
        </Row>

        <TaskCardFooterWrapper>
          <TaskCardFooterLeft>
            <FooterAssigneeWithDate task={task} />
            <TaskFooterAdditionalDetails task={task} />
          </TaskCardFooterLeft>
          <TaskCardFooterRight>
            <AvatarGroup maxCount={2}>
              {task.responsible.map((company: Company) => (
                <Avatar
                  size="small"
                  key={company.companyId}
                  name={company.companyName}
                  src={company.companyLogoUrl}
                />
              ))}
            </AvatarGroup>
          </TaskCardFooterRight>
        </TaskCardFooterWrapper>
      </ContentWrapper>
    </Card>
  );
};

export const TaskCard: FC<{
  task: Task;
  filterFunction: (task: Task) => boolean;
}> = ({ task, filterFunction }) => {
  const selectedStatusOption = useSelectedStatusOption(task.fields);
  const { isExportUI } = useIconTypeContext();

  if (isExportUI) {
    return (
      <TaskCardV2
        task={task}
        forExport={isExportUI}
        filterFunction={filterFunction}
      />
    );
  }

  if (selectedStatusOption?.group === StatusGroup.Completed) {
    return <ComplatedTaskCard task={task} />;
  }

  return <TaskCardV2 task={task} filterFunction={filterFunction} />;
};
